import React, { useState, useRef, useEffect } from 'react';

const AIAssistant = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: "Hello! I'm your AI Career Assistant. I can help you with resume reviews, interview preparation, skill analysis, and job search strategies. How can I assist you today?",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const quickActions = [
    {
      id: 1,
      title: 'Review My Resume',
      description: 'Get personalized feedback on your resume',
      icon: 'fas fa-file-alt',
      prompt: 'Can you review my resume and suggest improvements for ATS optimization?'
    },
    {
      id: 2,
      title: 'Interview Preparation',
      description: 'Practice questions for your next interview',
      icon: 'fas fa-comments',
      prompt: 'Generate technical interview questions for a React developer position'
    },
    {
      id: 3,
      title: 'Skill Gap Analysis',
      description: 'Identify skills needed for your target roles',
      icon: 'fas fa-chart-line',
      prompt: 'Analyze my current skills and suggest what I should learn next for frontend development roles'
    },
    {
      id: 4,
      title: 'Job Search Strategy',
      description: 'Get personalized job search advice',
      icon: 'fas fa-search',
      prompt: 'Suggest a job search strategy for remote frontend positions'
    },
    {
      id: 5,
      title: 'Cover Letter Help',
      description: 'Generate tailored cover letters',
      icon: 'fas fa-envelope',
      prompt: 'Help me write a cover letter for a senior frontend developer position'
    },
    {
      id: 6,
      title: 'Salary Negotiation',
      description: 'Prepare for salary discussions',
      icon: 'fas fa-handshake',
      prompt: 'How should I negotiate salary for a $120k frontend developer offer?'
    }
  ];

  const simulateAIResponse = (userMessage) => {
    const responses = {
      'resume': {
        content: `Based on your profile, here are my resume suggestions:

**🔍 Key Improvements:**
• **Quantify achievements** - Instead of "Built React components", try "Built 15+ reusable React components improving development speed by 40%"
• **Add relevant keywords** - Include: "TypeScript", "State Management", "Performance Optimization", "CI/CD"
• **Highlight leadership** - Mention any mentoring or team leadership experience

**📝 Recommended Action Items:**
1. Add 3-4 measurable achievements in your experience section
2. Include a technical skills section with proficiency levels
3. Tailor your summary to highlight full-stack capabilities

Would you like me to generate specific bullet points for your experience?`,
        type: 'ai'
      },
      'interview': {
        content: `Here are common technical interview questions for React positions:

**🧠 Technical Questions:**
• Explain the Virtual DOM and how React uses it
• What are React Hooks and when would you use them?
• How do you optimize React application performance?
• Difference between state and props

**💻 Coding Challenges:**
• Build a reusable modal component
• Implement a search filter for a list
• Create a custom hook for API calls
• Optimize a slow-rendering component

**🎯 Behavioral Questions:**
• Tell me about a challenging technical problem you solved
• How do you handle disagreements with team members?
• Describe your experience with code reviews

Would you like me to conduct a mock interview with you?`,
        type: 'ai'
      },
      'skills': {
        content: `**📊 Your Current Skill Analysis:**

**✅ Strong Areas:**
• React.js & JavaScript (Advanced)
• HTML/CSS (Advanced)
• Git & Version Control (Intermediate)

**📈 Recommended Skills to Learn:**
• **TypeScript** - Essential for modern React development
• **Next.js** - Highly demanded framework
• **Testing** - Jest & React Testing Library
• **State Management** - Redux Toolkit or Zustand

**🎯 Learning Path:**
1. Complete TypeScript fundamentals (2-3 weeks)
2. Build a project with Next.js (3-4 weeks)
3. Add testing to your existing projects (2 weeks)
4. Learn advanced React patterns (4 weeks)

Would you like specific resource recommendations?`,
        type: 'ai'
      },
      'default': {
        content: `I understand you're asking about career development. Here's how I can help you:

**🎯 Immediate Actions:**
1. **Resume Optimization** - Let me review your resume for ATS compatibility
2. **Interview Prep** - Practice technical and behavioral questions
3. **Skill Development** - Create a personalized learning plan
4. **Job Search** - Develop a targeted job search strategy

**💡 Pro Tip:** Keep your profile updated with recent projects and skills to get more accurate advice from me.

What specific area would you like to focus on today?`,
        type: 'ai'
      }
    };

    // Simple keyword matching for demo
    if (userMessage.toLowerCase().includes('resume') || userMessage.toLowerCase().includes('cv')) {
      return responses.resume;
    } else if (userMessage.toLowerCase().includes('interview') || userMessage.toLowerCase().includes('prepare')) {
      return responses.interview;
    } else if (userMessage.toLowerCase().includes('skill') || userMessage.toLowerCase().includes('learn')) {
      return responses.skills;
    } else {
      return responses.default;
    }
  };

  const handleSendMessage = async (message = inputMessage) => {
    if (!message.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Simulate AI thinking time
    setTimeout(() => {
      const aiResponse = simulateAIResponse(message);
      const aiMessage = {
        id: messages.length + 2,
        type: aiResponse.type,
        content: aiResponse.content,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, aiMessage]);
      setIsLoading(false);
    }, 1500);
  };

  const handleQuickAction = (prompt) => {
    handleSendMessage(prompt);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatMessage = (content) => {
    return content.split('\n').map((line, index) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return <strong key={index} className="text-gray-800">{line.replace(/\*\*/g, '')}</strong>;
      } else if (line.startsWith('•') || line.startsWith('1.') || line.startsWith('2.') || line.startsWith('3.')) {
        return <div key={index} className="flex items-start ml-4"><span className="mr-2">•</span>{line.substring(1)}</div>;
      } else if (line.trim() === '') {
        return <br key={index} />;
      } else {
        return <div key={index}>{line}</div>;
      }
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">AI Career Assistant</h1>
        <p className="text-gray-400">Your personal AI coach for resume reviews, interview prep, and career guidance</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Chat Interface - 3/4 width */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow-md">
            {/* Chat Header */}
            <div className="border-b border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <i className="fas fa-robot text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">AI Career Assistant</h3>
                  <p className="text-sm text-gray-600">Online - Ready to help</p>
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <div className="h-96 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-3/4 rounded-lg p-4 ${
                      message.type === 'user'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    <div className="whitespace-pre-wrap">
                      {formatMessage(message.content)}
                    </div>
                    <div className={`text-xs mt-2 ${message.type === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-gray-600">AI is thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="border-t border-gray-200 p-4">
              <div className="flex gap-3">
                <div className="flex-1">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask me anything about your career, resume, interviews..."
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows="2"
                  />
                </div>
                <button
                  onClick={() => handleSendMessage()}
                  disabled={!inputMessage.trim() || isLoading}
                  className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed self-end mb-4"
                >
                  <i className="fas fa-paper-plane"></i>
                </button>
              </div>
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-gray-500">
                  Press Enter to send, Shift+Enter for new line
                </p>
                <button className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1">
                  <i className="fas fa-paperclip"></i>
                  Attach Resume
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions - 1/4 width */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              {quickActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => handleQuickAction(action.prompt)}
                  disabled={isLoading}
                  className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className={`${action.icon} text-blue-500`}></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{action.title}</p>
                      <p className="text-xs text-gray-600">{action.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Tips Section */}
            <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h4 className="font-medium text-yellow-800 text-sm mb-2 flex items-center gap-2">
                <i className="fas fa-lightbulb"></i>
                Pro Tips
              </h4>
              <ul className="text-xs text-yellow-700 space-y-1">
                <li>• Be specific about your target roles</li>
                <li>• Share your resume for personalized advice</li>
                <li>• Ask about interview preparation</li>
                <li>• Request skill gap analysis</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;